import { request, response } from "../../proto/bundle";
import { getConnectionPool } from "../../core/connectionPool";
import { File } from "./File";
import Long from "long";
import { checkMigrate, globalCache } from "../../init/sdkInit";
import { worker } from "../..";

export function search_sizes(sizes: (number | Long)[] | null | undefined,
    versions: Uint8Array<ArrayBufferLike>[] | null | undefined,
    currentVersion: Uint8Array<ArrayBufferLike> | null | undefined): number {
    if (!sizes || !versions || !currentVersion) {
        throw new Error("Sizes, versions or current version is null or undefined");
    }
    for (let i = 0; i < sizes.length; i++) {
        if (versions[i] && versions[i].byteLength > 0) {
            if (versions[i].byteLength === currentVersion.byteLength &&
                versions[i].every((byte, index) => byte === currentVersion[index])) {
                let size = sizes[i];
                if (typeof size === "number") {
                    return size;
                } else {
                    return size.toNumber();
                }
            }
        }
    }
    throw new Error("Can't find current version in versions");
}

export function getListOfItems(items: response.IMetaObj[], parent_path?: string): (Folder | File)[] {
    const itemList: (Folder | File)[] = [];
    for (const item of items) {
        if (item.fileMeta) {
            const file = new File();
            file.setFileUuid(item.fileMeta.uuid ?? new Uint8Array(0));
            file.setParentPath(parent_path ?? item.fileMeta.parentFolder ?? "/");
            file.setFileName(item.fileMeta.name ?? "");
            file.setNew(false);

            file.setSize(search_sizes(item.fileMeta.sizes, item.fileMeta.versions, item.fileMeta.currentVersion));
            file.setSizes(item.fileMeta.sizes ?? []);
            file.setVersions(item.fileMeta.versions ?? []);
            file.setVersionUuid(item.fileMeta.currentVersion ?? new Uint8Array(0));
            file.setCurrentVersion(item.fileMeta.currentVersion ?? new Uint8Array(0));

            file.setCreationDate(item.fileMeta.creationDate ?? 0);
            file.setLastModifiedDate(item.fileMeta.lastModifiedDate ?? 0);
            file.setOwner(item.fileMeta.owner ?? "");
            itemList.push(file);
        }
        else if (item.folderMeta) {
            const folder = new Folder();
            folder.setUuId(item.folderMeta.uuid ?? new Uint8Array(0));
            folder.setFolderName(item.folderMeta.name ?? "");
            folder.setParentPath(parent_path ?? item.folderMeta.parentFolder ?? "/");
            folder.setCreationDate(item.folderMeta.creationDate ?? 0);
            folder.setCurrentPage(0);
            folder.setLastModifiedDate(item.folderMeta.lastModifiedDate ?? 0);
            folder.setOwner(item.folderMeta.owner ?? "");
            folder.setOrderBy(0);
            itemList.push(folder);
        }
        else {
            throw new Error(`Unknown item type in folder list at path ${this.parentPath}/${this.folderName}`);
        }
    }
    return itemList;
}

export type regexOptions = request.IRegexOptions;
export class Folder {
    private folderName!: string;
    private folderUuId!: Uint8Array;
    private parentPath!: string;
    private creationDate!: number | Long;
    private page_token_array: Uint8Array[] = [];
    private totalItems: number | Long = 0;
    private pageSize: number = 50;
    private path: string = "/";
    private currentPage: number = 0;
    private lastModifiedDate!: number | Long;
    private owner!: string;
    private orderBy!: number;
    private size: (number | Long) = 0;
    private in_trash: boolean = false;
    private type_of_path: number = 0;
    private regex: regexOptions | null = null;

    setFolderName(name: string) {
        this.folderName = name;
    }
    setUuId(uuid: Uint8Array) {
        this.folderUuId = uuid;
    }
    setParentPath(path: string) {
        this.parentPath = path;
    }
    setCreationDate(date: number | Long) {
        this.creationDate = date;
    }
    setCurrentPage(page: number) {
        this.currentPage = page;
    }
    setLastModifiedDate(date: number | Long) {
        this.lastModifiedDate = date;
    }
    setOwner(owner: string) {
        this.owner = owner;
    }
    setOrderBy(order: number) {
        this.orderBy = order;
    }

    setSize(size: number | Long) {
        this.size = size;
    }

    getFolderName() {
        return this.folderName;
    }
    getParentPath() {
        return this.parentPath;
    }
    getCreationDate() {
        return this.creationDate;
    }
    getCurrentPage() {
        return this.currentPage;
    }
    getLastModifiedDate() {
        return this.lastModifiedDate;
    }
    getOwner() {
        return this.owner;
    }
    getOrderBy() {
        return this.orderBy;
    }
    getpath() {
        return this.path;
    }

    setInTrash(inTrash: boolean) {
        this.in_trash = inTrash;
    }

    isInTrash() {
        return this.in_trash;
    }

    getTotalItems(): number {
        if (this.totalItems instanceof Long) {
            return this.totalItems.toNumber();
        }
        return this.totalItems;
    }

    getPageSize(): number {
        return this.pageSize;
    }

    getUuId(): string {
        if (!this.folderUuId) return "";
        return Array.from(this.folderUuId)
            .map(byte => byte.toString(16).padStart(2, "0"))
            .join("");
    }

    getSize(): number {
        if (this.size instanceof Long) {
            return this.size.toNumber();
        }
        return this.size;
    }

    getCreationDateString(): string {
        if (this.creationDate instanceof Long) {
            return new Date(this.creationDate.toNumber() * 1000).toISOString().replace('T', ' ').substring(0, 19);
        } else {
            return new Date(this.creationDate * 1000).toISOString().replace('T', ' ').substring(0, 19);
        }

    }

    getLastModifiedDateString(): string {
        if (this.lastModifiedDate instanceof Long) {
            return new Date(this.lastModifiedDate.toNumber() * 1000).toISOString().replace('T', ' ').substring(0, 19);
        } else {
            return new Date(this.lastModifiedDate * 1000).toISOString().replace('T', ' ').substring(0, 19);
        }
    }


    getRoot() {
        const folder = new Folder();
        folder.setFolderName("/");
        folder.setParentPath("");
        folder.setCreationDate(0);
        folder.setCurrentPage(-1);
        folder.setLastModifiedDate(0);
        folder.setOwner("");
        folder.setOrderBy(0);
        folder.setUuId(new Uint8Array(0));
        return folder;
    }

    /**
     * Creates a new folder at the specified parent path with the given folder name.
     * @param parentPath - The path where the new folder should be created.
     * @param folderName - The name of the new folder.
     * @returns A promise that resolves to the created Folder object.
     */
    async createFolder(parentPath: string, folderName: string): Promise<Folder> {
        let pool = await getConnectionPool();
        let parentPathTrimmed = parentPath.endsWith("/") ? parentPath.slice(0, -1) : parentPath;
        let req = request.Request.create({
            id: null,
            reqCreateTime: Date.now(),
            createFolder: request.CreateFolder.create({
                parentPath: parentPathTrimmed,
                name: folderName,
                typeOfPath: 0
            })
        });
        let resp = await pool.sendAndWait(req);
        checkMigrate(resp?.createFolder?.status)
        if (resp.createFolder?.status !== 0) {
            throw new Error(`Failed to create folder at path ${parentPath}/${folderName}`);
        }
        let folder = new Folder();
        folder.setFolderName(folderName);
        folder.setParentPath(parentPathTrimmed);
        folder.setCreationDate(Date.now());
        folder.setCurrentPage(-1);
        folder.setLastModifiedDate(Date.now());
        folder.setOwner("");
        folder.setOrderBy(0);
        return folder;
    }

    /**
     * Batch-creates multiple folders by firing all requests on a single connection
     * without waiting between sends, then collecting all responses.
     * @param folders - Array of { parentPath, folderName } to create.
     * @returns Array of results with success/failure for each folder.
     */
    async createFolders(folders: { parentPath: string; folderName: string }[]): Promise<{ parentPath: string; folderName: string; success: boolean; error?: string }[]> {
        if (folders.length === 0) return [];

        const pool = await getConnectionPool();

        const requests = folders.map(({ parentPath, folderName }) => {
            const parentPathTrimmed = parentPath.endsWith("/") ? parentPath.slice(0, -1) : parentPath;
            return request.Request.create({
                id: null,
                reqCreateTime: Date.now(),
                createFolder: request.CreateFolder.create({
                    parentPath: parentPathTrimmed,
                    name: folderName,
                    typeOfPath: 0
                })
            });
        });

        const responses = await pool.sendBatchAndWaitAll(requests);

        return responses.map((resp, i) => {
            const { parentPath, folderName } = folders[i];
            checkMigrate(resp?.createFolder?.status);
            if (resp.createFolder?.status !== 0) {
                return { parentPath, folderName, success: false, error: `Failed to create folder at path ${parentPath}/${folderName}` };
            }
            return { parentPath, folderName, success: true };
        });
    }
    /**
     * Removes the folder at the specified path.
     * @param is_prem - If true, the folder will be permanently deleted. only works if the folder is in trash.
     * @returns A promise that resolves when the folder is removed.
     */
    async remove(is_prem: boolean): Promise<void> {
        // console.log(`Removing folder at path: ${this.parentPath}/${this.folderName}, is_prem: ${is_prem}`);
        const pool = await getConnectionPool();
        const req = request.Request.create({
            id: null,
            reqCreateTime: Date.now(),
            removeFolder: request.RemoveFolder.create({
                folderFullPath: this.parentPath + "/" + this.folderName,
                isPerm: is_prem
            })
        })
        const resp = await pool.sendAndWait(req);
        checkMigrate(resp.removeFolder.status)
        if (resp.removeFolder?.status !== 0) {
            throw new Error(`Failed to remove folder at path ${this.parentPath}/${this.folderName} with status code ${resp.removeFolder?.status} `);
        }
        return;
    }
    /**
     * Moves the folder to a new path with an optional new name.
     * @param new_path - The destination path where the folder should be moved.
     * @param new_name - Optional new name for the folder.
     * @returns A promise that resolves when the folder is moved.
     */
    async move(new_path: string, new_name?: string): Promise<void> {
        const pool = await getConnectionPool();
        const parentPath = new_path.endsWith("/") ? new_path : new_path + "/";
        const folderName = new_name || this.folderName;
        const req = request.Request.create({
            id: null,
            reqCreateTime: Date.now(),
            moveFolder: request.MoveFolder.create({
                fileFullPath: this.parentPath + "/" + this.folderName,
                destinationParentPath: new_path,
                newFileName: folderName
            })
        });
        const resp = await pool.sendAndWait(req);
        checkMigrate(resp.moveFolder.status)
        if (resp.moveFolder?.status !== 0) {
            throw new Error(`Failed to move folder from ${this.parentPath}/${this.folderName} to ${new_path}/${new_name}`);
        }
        return;
    }

    /**
     * Renames the folder to a new name.
     * @param new_name - The new name for the folder.
     * @returns A promise that resolves when the folder is renamed.
     */
    async rename(new_name: string): Promise<void> {
        const pool = await getConnectionPool();
        const req = request.Request.create({
            id: null,
            reqCreateTime: Date.now(),
            renameFolder: request.RenameFolder.create({
                folderPath: this.parentPath + "/" + this.folderName,
                newName: new_name
            })
        });
        const resp = await pool.sendAndWait(req);
        checkMigrate(resp.renameFolder.status)
        if (resp.renameFolder?.status !== 0) {
            throw new Error(`Failed to rename folder from ${this.folderName} to ${new_name}`);
        }
        this.setFolderName(new_name);
    }

    /**
     * Restores the folder from trash.
     * @returns A promise that resolves when the folder is restored.
     */
    async restore(): Promise<void> {
        const pool = await getConnectionPool();
        const req = request.Request.create({
            id: null,
            reqCreateTime: Date.now(),
            untrashFolder: request.UntrashFolder.create({
                folderFullPath: this.parentPath + "/" + this.folderName
            })
        });
        const resp = await pool.sendAndWait(req);
        checkMigrate(resp.untrashFolder.status)
        if (resp.untrashFolder?.status !== 0) {
            throw new Error(`Failed to restore folder at path ${this.parentPath}/${this.folderName}`);
        }
        return;
    }

    /**
     * Creates a folder object with the specified full path.
     * @param fullPath - The full path of the folder.
     * @returns A promise that resolves to a Folder object.
     */
    folderWithName(fullPath: string): Folder {
        let parentPath = fullPath.endsWith("/") ? fullPath.slice(0, -1) : fullPath;
        let folderName = parentPath.substring(parentPath.lastIndexOf("/") + 1);
        parentPath = parentPath.substring(0, parentPath.lastIndexOf("/"));
        const folder = new Folder();
        folder.setFolderName(folderName);
        folder.setParentPath(parentPath);
        folder.setCreationDate(0);
        folder.setCurrentPage(-1);
        folder.setLastModifiedDate(0);
        folder.setOwner("");
        folder.setOrderBy(0);
        folder.setUuId(new Uint8Array(0));
        return folder;
    }


    async exists(): Promise<boolean> {
        const pool = await getConnectionPool();
        const req = request.Request.create({
            id: null,
            reqCreateTime: Date.now(),
            getMetaFromPath: request.GetMetaFromPath.create({
                path: this.parentPath + "/" + this.folderName,
                trashed: this.in_trash,
                typeOfPath: 0

            })
        });
        const resp = await pool.sendAndWait(req);
        checkMigrate(resp.getMetaFromPath.status)
        if (resp.getMetaFromPath?.status === 0) {

            if (resp.getMetaFromPath.meta?.folderMeta) {
                this.setCreationDate(resp.getMetaFromPath.meta.folderMeta.creationDate ?? 0);
                this.setLastModifiedDate(resp.getMetaFromPath.meta.folderMeta.lastModifiedDate ?? 0);
                this.setOwner(resp.getMetaFromPath.meta.folderMeta.owner ?? "");
                return true; // Folder exists
            } else if (resp.getMetaFromPath.meta?.fileMeta) {
                return false; // A file exists at this path, not a folder
            } else {
                return false; // Folder does not exist
            }

        } else {
            throw new Error(`Failed to check existence of folder at path ${this.parentPath}/${this.folderName}`);
        }
    }

    private clearCachePath(path: string) {
        globalCache.delete(path + "-1".toString())
        if (globalCache.has(path + "-totalItems")) {
            const items = globalCache.get(path + "-totalItems");
            globalCache.delete(path + "-totalItems")
            const totalPages = Math.ceil(Number(items) / this.pageSize);
            for (let i = 0; i < totalPages + 1; i++) {
                globalCache.delete(path + i.toString());
            }
        }
        const escapedPath = path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        globalCache.deleteByPattern(new RegExp(`^${escapedPath}-totalItems-regex`));
        globalCache.deleteByPattern(new RegExp(`^${escapedPath}\\d+-regex`));
    }

    async getPreviousFolderList(currentPage: number): Promise<(File | Folder)[]> {
        if (currentPage <= 0) {
            throw new Error("Already at the first page, cannot go back further.");
        }
        let output = await this.listCall(currentPage - 1)
        let list = output.list
        return list;
    }

    /**
     * Retrieves a list of files and folders in the current folder.
     * @returns A promise that resolves to an array of File or Folder objects.
     */

    async getFolderList(order?: number, regex?: regexOptions): Promise<(File | Folder)[]> {
        //this.OrderBy if undefined, default to 0 (by name)
        const orderBy = order ?? 0;
        this.orderBy = orderBy;

        if (this.in_trash && regex) {
            throw new Error("Regex is not supported on trash");
        }
        const rawPath = this.in_trash ? "/trash/" : ("/files/" + this.parentPath + "/" + this.folderName);
        const path = rawPath.replace(/\/{2,}/g, "/");
        this.path = path
        this.clearCachePath(path)
        this.orderBy = order ?? 0
        this.setRegex(regex)

        let output = await this.listCall(-1)
        let resp = output.resp
        this.totalItems = (resp.list.totalCount) ?? 0;
        let cache_key = path + "-totalItems"
        if (this.regex) {
            cache_key += "-regex-" + JSON.stringify(this.regex)
        }
        globalCache.set(cache_key, this.totalItems)
        this.page_token_array = new Array(Math.ceil(Number(this.totalItems) / this.pageSize) + 1)
        this.currentPage = 0
        this.page_token_array[this.currentPage] = (resp?.list.pageToken)

        let outpu2 = await this.listCall(0)
        resp = outpu2.resp
        let list = outpu2.list
        if (resp == null) {
            return list;
        }

        (worker && worker.postMessage({
            op: "startFolderList",
            folder: this,
            page_token: resp?.list?.pageToken
        }))
        return list;
    }

    async getSpacesFolderList(order?: number, regex?: regexOptions): Promise<(File | Folder)[]> {
        //this.OrderBy if undefined, default to 0 (by name)
        const orderBy = order ?? 0;
        this.orderBy = orderBy;

        const path = this.parentPath + "/" + this.folderName;
        this.path = path
        this.clearCachePath(path)
        this.orderBy = order ?? 0
        if (regex) {
            throw new Error("Regex is not supported on spaces folder list");
        }
        let output = await this.listCall(-1)
        let resp = output.resp
        this.totalItems = (resp.list.totalCount) ?? 0;
        globalCache.set(path + "-totalItems", this.totalItems)

        this.page_token_array = new Array(Math.ceil(Number(this.totalItems) / this.pageSize) + 1)
        this.currentPage = 0
        this.page_token_array[this.currentPage] = (resp?.list.pageToken)

        let outpu2 = await this.listCall(0)
        resp = outpu2.resp
        let list = outpu2.list
        if (resp == null) {
            return list;
        }
        // worker.postMessage({
        //     op: "startFolderList",
        //     folder : this,
        //     page_token: resp?.list.pageToken
        // })
        return list;
    }


    async getSharedWithMeFolderList(isTeam: boolean, regex?: regexOptions): Promise<(File | Folder)[]> {
        // console.log("getSharedWithMeFolderList is called in sdk");
        // console.log("isTeam:", this.parentPath);
        const orderBy = 2;
        let path = this.parentPath + "/" + this.folderName;
        this.path = path
        let tp = 0;
        if (isTeam) {
            tp = 2;
        }
        this.clearCachePath(path)
        this.orderBy = orderBy ?? 0
        if (regex) {
            throw new Error("Regex is not supported on shared with me folder list");
        }
        this.type_of_path = tp
        let output = await this.listCall(-1)
        let resp = output.resp
        this.totalItems = (resp.list.totalCount) ?? 0;
        globalCache.set(path + "-totalItems", this.totalItems)

        this.page_token_array = new Array(Math.ceil(Number(this.totalItems) / this.pageSize) + 1)
        this.currentPage = 0
        this.page_token_array[this.currentPage] = (resp?.list.pageToken)

        let outpu2 = await this.listCall(0)
        resp = outpu2.resp
        let list = outpu2.list
        if (resp == null) {
            return list;
        }
        (worker && worker.postMessage({
            op: "startFolderList",
            folder: this,
            page_token: resp?.list.pageToken
        }))
        return list;
    }


    async getNextFolderList(currentPage: number): Promise<(File | Folder)[]> {
        let output = await this.listCall(currentPage)
        let list = output.list
        return list;
    }

    unsetRegex() {
        this.regex = null;
    }
    setRegex(regex: regexOptions) {
        this.regex = regex;
    }

    // TODO: as pages increase cache size also increases without limit. Need to implement a better caching strategy.
    private async listCall(page: number): Promise<{ list: (File | Folder)[]; resp: response.Response | null; }> {
        let path = this.path
        let type_of_path = this.type_of_path
        if (this.orderBy === undefined) {
            this.orderBy = 0
        }
        let order = this.orderBy
        let regex = this.regex
        let pageToken: Uint8Array
        let cache_key = path + page.toString();
        let path_page_key = path + (page - 1).toString()
        if (this.regex) {
            path = "";
            const regexStr = JSON.stringify(this.regex);
            cache_key += "-regex-" + regexStr;
            path_page_key += "-regex-" + regexStr;
        }
        if (page >= 0) {
            if (globalCache.has(cache_key)) {
                const data = globalCache.get(cache_key);
                return { list: data.list, resp: data.resp };
            } else {
                // Need the previous page's pageToken. If it isn't cached yet,
                // fetch it synchronously instead of firing-and-forgetting (which
                // would NPE on `pageTokenObject.token` below).
                let pageTokenObject = globalCache.get(path_page_key);
                if (!pageTokenObject) {
                    await this.listCall(page - 1);
                    pageTokenObject = globalCache.get(path_page_key);
                }
                pageToken = pageTokenObject?.token ?? null;
            }
        } else {
            pageToken = null
        }
        const pool = await getConnectionPool();
        const req = request.Request.create({
            id: null,
            reqCreateTime: Date.now(),
            list: request.List.create({
                path: path,
                type: request.ListType.FilesAndFolders,
                pageSize: this.pageSize,
                orderBy: order,
                pageToken: pageToken,
                typeOfPath: type_of_path,
            })
        });
        if (regex) {
            req.list!.regexOptions = regex;
            req.list!.path = null
        }
        const resp = await pool.sendAndWait(req);
        checkMigrate(resp.list.status)
        if (resp.list?.status !== 0) {
            throw new Error(`Failed to list folder at path ${this.parentPath}/${this.folderName}`);
        }
        const list = getListOfItems(resp.list.objects ?? [], regex ? undefined : this.parentPath + "/" + this.folderName);
        globalCache.set(cache_key, {
            "list": list,
            "token": resp.list?.pageToken ?? null
        })
        return { list: list, resp: resp }
    }

    async getFolderChildrenNames(): Promise<string[]> {
        const orderBy = 2;
        const pool = await getConnectionPool();
        const path = this.in_trash ? "/trash/" : ("/files/" + this.parentPath + "/" + this.folderName);

        let page_token = null;
        const names: string[] = [];
        while (true) {
            const req = request.Request.create({
                id: null,
                reqCreateTime: Date.now(),
                list: request.List.create({
                    path: path,
                    type: request.ListType.FilesAndFolders,
                    pageSize: this.pageSize,
                    orderBy: orderBy,
                    pageToken: page_token,
                    typeOfPath: 0,
                    onlyFolders: true,
                    onlyNames: true,
                })
            });
            const resp = await pool.sendAndWait(req);
            checkMigrate(resp.list.status)
            if (resp.list?.status !== 0) {
                throw new Error(`Failed to list folder at path ${this.parentPath}/${this.folderName}`);
            }
            for (const item of resp.list.objects ?? []) {
                if (item.nameMeta) {
                    names.push(item.nameMeta.name ?? "Default");
                } else {
                    throw new Error(`Unknown item type in folder list at path ${this.parentPath}/${this.folderName}`);
                }
            }
            page_token = resp.list.pageToken ?? null;
            if (page_token === null || page_token.length === 0) {
                break;
            }
            req.list!.pageToken = page_token;
        }
        return names;
    }

    /**
     * Uploads a local folder to the specified destination path.
     * @param localFolderPath - The path to the local folder to upload.
     * @param destinationPath - The destination path in the remote storage.
     * @param onProgress - Optional callback to track upload progress.
     */

    // async upload(
    //     localFolderPath: string,
    //     destinationPath: string,
    //     onProgress?: (progress: { file: string; loaded: number; total: number }) => void
    // ) {
    //     const stats = fs.statSync(localFolderPath);
    //     if (!stats.isDirectory()) {
    //         throw new Error(`${localFolderPath} is not a directory`);
    //     }

    //     const baseName = path.basename(localFolderPath);
    //     const baseRemotePath = path.join(destinationPath, baseName).replace(/\\/g, "/");

    //     // Create top-level folder
    //     await this.createFolder(destinationPath, baseName);

    //     // Collect all file paths and their sizes first
    //     const files: { fullPath: string; size: number }[] = [];
    //     const scanFiles = (dir: string) => {
    //         for (const entry of fs.readdirSync(dir, { withFileTypes: true })) {
    //             const full = path.join(dir, entry.name);
    //             if (entry.isDirectory()) {
    //                 scanFiles(full);
    //             } else {
    //                 const size = fs.statSync(full).size;
    //                 files.push({ fullPath: full, size });
    //             }
    //         }
    //     };
    //     scanFiles(localFolderPath);

    //     const totalSize = files.reduce((sum, f) => sum + f.size, 0);
    //     let loadedSoFar = 0;

    //     const walkAndUpload = async (currentLocalPath: string, currentRemotePath: string) => {
    //         const entries = fs.readdirSync(currentLocalPath, { withFileTypes: true });

    //         for (const entry of entries) {
    //             const fullLocalPath = path.join(currentLocalPath, entry.name);
    //             const remotePath = path.join(currentRemotePath, entry.name).replace(/\\/g, "/");

    //             if (entry.isDirectory()) {
    //                 await this.createFolder(currentRemotePath, entry.name);
    //                 await walkAndUpload(fullLocalPath, remotePath);
    //             } else if (entry.isFile()) {
    //                 const file = new File();
    //                 const parentRemotePath = currentRemotePath;
    //                 let lastLoaded = 0;

    //                 await file.uploadFile(fullLocalPath, parentRemotePath, (progress) => {
    //                     const delta = progress.loaded - lastLoaded;
    //                     lastLoaded = progress.loaded;
    //                     loadedSoFar += delta;
    //                     if (onProgress) {
    //                         onProgress({
    //                             file: fullLocalPath,
    //                             loaded: loadedSoFar,
    //                             total: totalSize,
    //                         });
    //                     }
    //                 });
    //             }
    //         }
    //     };

    //     await walkAndUpload(localFolderPath, baseRemotePath);
    // }

    /**
 * Recursively copies a folder and its contents to a new destination.
 * @param destinationPath - The full path where this folder should be copied.
 * @param newName - Optional new name for the copied folder.
 */
    async copy(
        destinationPath: string,
        newName?: string,
        progress?: (progress: { file: string; current: number; total: number }) => void
    ): Promise<void> {
        const newFolderName = newName ?? this.folderName;
        const originalFullPath = this.parentPath + "/" + this.folderName;

        type CopyEntry =
            | { type: "folder"; fullPath: string; folder: Folder }
            | { type: "file"; fullPath: string; file: File };

        const entries: CopyEntry[] = [];
        const walk = async (srcFolder: Folder, currentFullPath: string) => {
            entries.push({ type: "folder", fullPath: currentFullPath, folder: srcFolder });
            let parentPath = this.parentPath;
            const parentPathParts = parentPath.split("/").filter(Boolean);
            let children;
            if (parentPathParts[0] == "shared") {
                children = await srcFolder.getSharedWithMeFolderList(false);
            } else {
                children = await srcFolder.getFolderList();
            }

            let i = 0
            const totalPages = Math.ceil(Number(srcFolder.getTotalItems()) / srcFolder.getPageSize());
            while (i < totalPages) {
                for (const item of children) {
                    if (item instanceof Folder) {
                        const itemPath = currentFullPath + "/" + item.getFolderName();
                        await walk(item, itemPath);
                    } else if (item instanceof File) {
                        const itemPath = currentFullPath + "/" + item.getName();
                        entries.push({ type: "file", fullPath: itemPath, file: item });
                    }
                }
                i += 1
                children = await srcFolder.getNextFolderList(i);
            }
        };


        await walk(this, originalFullPath);
        const total = entries.length;
        let current = 0;

        // Step 2: Copy
        for (const entry of entries) {
            const relativePath = entry.fullPath.replace(originalFullPath, "").replace(/^\/+/, "");
            const fullPath = `${destinationPath}/${newFolderName}/${relativePath}`.replace(/\/+/g, "/");
            const targetFullPath = fullPath.endsWith("/") && fullPath.length > 1
                ? fullPath.slice(0, -1)
                : fullPath;
            if (entry.type === "folder") {
                const lastSlashIndex = targetFullPath.lastIndexOf("/");
                const parentPath = lastSlashIndex !== -1 ? targetFullPath.substring(0, lastSlashIndex) : "";
                const folderName = lastSlashIndex !== -1 ? targetFullPath.substring(lastSlashIndex + 1) : targetFullPath;
                await entry.folder.createFolder(parentPath, folderName);
                current++;
                progress?.({ file: `${targetFullPath}/`, current, total });
            } else if (entry.type === "file") {
                const lastSlashIndex = targetFullPath.lastIndexOf("/");
                const parentPath = lastSlashIndex !== -1 ? targetFullPath.substring(0, lastSlashIndex) : "";
                await entry.file.copy(parentPath);
                current++;
                progress?.({ file: targetFullPath, current, total });
            }
        }
    }



};
